import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movieId = parseInt(id || '1', 10);

  const movie = useMemo(() => movies.find((m) => m.id === movieId), [movieId]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && m.category === movie.category)
      .slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center mb-4">
            <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-500 text-lg">影片未找到</p>
          <Link
            to="/"
            className="mt-4 inline-block text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const categoryName = categories.find((c) => c.slug === movie.category)?.name || '';

  return (
    <main>
      <article className="w-full px-4 md:px-6 pt-24 pb-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-72 shrink-0">
            <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-100">
              <img
                src={movie.posterUrl}
                alt={movie.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
              {movie.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="bg-accent-100 text-accent-700 text-xs font-semibold px-2.5 py-1 rounded-md flex items-center gap-1 whitespace-nowrap">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-star-fill"></i>
                </div>
                {movie.rating}
              </span>
              <span className="text-sm text-foreground-500">{movie.year}</span>
              <span className="text-sm text-foreground-500">{movie.genre}</span>
              <span className="text-sm text-foreground-500">{movie.duration}</span>
              {categoryName && (
                <Link
                  to={`/category/${movie.category}`}
                  className="text-sm text-primary-600 hover:text-primary-700 bg-primary-50 px-2.5 py-0.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
                >
                  {categoryName}
                </Link>
              )}
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-semibold text-foreground-800 mb-2">导演</h3>
              <p className="text-sm text-foreground-600">{movie.director}</p>
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-semibold text-foreground-800 mb-2">主演</h3>
              <div className="flex flex-wrap gap-2">
                {movie.actors.map((actor) => (
                  <span
                    key={actor}
                    className="bg-background-100 text-foreground-600 text-xs px-3 py-1 rounded-full whitespace-nowrap"
                  >
                    {actor}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-sm font-semibold text-foreground-800 mb-2">剧情简介</h3>
              <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl">
                {movie.description}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-play-circle-fill text-lg"></i>
                </div>
                立即播放
              </button>
              <button className="bg-background-100 hover:bg-background-200 text-foreground-700 px-8 py-3 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-heart-line"></i>
                </div>
                收藏
              </button>
            </div>
          </div>
        </div>
      </article>

      {relatedMovies.length > 0 && (
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">
            相关推荐
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {relatedMovies.map((m) => (
              <MovieCard
                key={m.id}
                id={m.id}
                title={m.title}
                year={m.year}
                rating={m.rating}
                genre={m.genre}
                posterUrl={m.posterUrl}
                isNew={m.isNew}
              />
            ))}
          </div>
        </section>
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            dateCreated: `${movie.year}`,
            director: {
              '@type': 'Person',
              name: movie.director,
            },
            actor: movie.actors.map((a) => ({
              '@type': 'Person',
              name: a,
            })),
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: movie.rating,
              bestRating: '10',
            },
          }),
        }}
      />
    </main>
  );
}